import i18n from '@i18n/i18n';
import { message } from 'antd';
import { getUserTimezone } from '@utils/dateTime';
import type { JsonObject } from '@services/apiSchemas';

const OPERATOR_BASE_URL = '/api/operator/v1/';

export interface OperatorFetchOptions extends Omit<RequestInit, 'body' | 'headers'> {
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  // body 接受三種形態：
  //   - `BodyInit`：FormData / Blob / String 等原生形態（不會被 JSON.stringify）
  //   - `JsonObject`：typed Input interface（`extends JsonObject`）— 嚴格 JSON-safe 值
  //   - `Record<string, unknown>`：dynamic Hash payload（form values 等沒有 sealed type）
  // 兩個物件型 union 是刻意保留 — JsonObject 限制 value 型別（強型別 caller 用），
  // Record<string, unknown> 接 dynamic Hash（避免每個 caller 都得 cast）。
  body?: BodyInit | JsonObject | Record<string, unknown> | null;
}

// operatorApiFetch - 用於 operator API 的請求
export const operatorApiFetch = async (
  url: string,
  options: OperatorFetchOptions = {}
): Promise<Response> => {
  const { headers: customHeaders, params, ...fetchOptions } = options;

  // 如果 URL 不是完整的 URL，則加上 OPERATOR_BASE_URL
  const normalizedUrl = url.startsWith('/') ? url.slice(1) : url;
  let fullUrl = url.startsWith('http') ? url : `${OPERATOR_BASE_URL}${normalizedUrl}`;

  // 自動處理 GET/HEAD 請求的 params
  if (params) {
    const urlObj = new URL(fullUrl, window.location.origin);
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach((val: unknown) => {
            const finalVal =
              val && typeof (val as { toISOString?: () => string }).toISOString === 'function'
                ? (val as { toISOString: () => string }).toISOString()
                : val;
            urlObj.searchParams.append(`${key}[]`, String(finalVal));
          });
        } else if (
          typeof value === 'object' &&
          typeof (value as { toISOString?: () => string }).toISOString !== 'function'
        ) {
          // Handle nested objects (e.g., date ranges: orders.created_at[start]=...)
          // Note: Exclude objects that are actually Dates/Dayjs (handled by else block if not nested, but logic here assumes structure)
          // Actually, params[key] being Dayjs would have toISOString, so check that first.
          // However, Dayjs is 'object'.

          const nested = value as Record<string, unknown>;
          Object.keys(nested).forEach((subKey) => {
            const subVal = nested[subKey];
            if (subVal !== undefined && subVal !== null) {
              const finalSubVal =
                subVal && typeof (subVal as { toISOString?: () => string }).toISOString === 'function'
                  ? (subVal as { toISOString: () => string }).toISOString()
                  : subVal;
              urlObj.searchParams.append(`${key}[${subKey}]`, String(finalSubVal));
            }
          });
        } else {
          const finalVal =
            value && typeof (value as { toISOString?: () => string }).toISOString === 'function'
              ? (value as { toISOString: () => string }).toISOString()
              : value;
          urlObj.searchParams.append(key, String(finalVal));
        }
      }
    });
    fullUrl = urlObj.toString();
  }

  // 獲取當前 locale 和 timezone
  const currentLocale = i18n.language || 'zh';
  const timezone = getUserTimezone();

  const headers: Record<string, string> = {
    'Accept-Language': currentLocale,
    'X-Timezone': timezone,
    ...customHeaders,
  };

  const config: OperatorFetchOptions = {
    headers,
    credentials: 'include', // 包含 cookies (JWT token stored in HTTP-only cookie)
    ...fetchOptions,
  };

  // 如果有 body 且不是 FormData，自動 JSON 序列化
  if (config.body && !(config.body instanceof FormData)) {
    if (typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }
    // 設定 Content-Type（無論是 object 還是已經 stringify 過的 string）
    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
  }

  try {
    const response = await fetch(fullUrl, config as RequestInit);

    // 處理 401 未授權錯誤
    if (response.status === 401) {
      // 在公開頁面（登入、邀請接受）不自動跳轉
      const isGuestPage =
        window.location.pathname.includes('/login') ||
        window.location.pathname.includes('/invitations/');
      if (!isGuestPage) {
        window.location.href = '/operator/login?reason=session_expired';
      }
      const body = await response
        .clone()
        .json()
        .catch(() => null);
      const error = new Error('Unauthorized') as Error & { status?: number; errorCode?: string };
      error.status = 401;
      if (body?.message) {
        error.errorCode = body.message;
      }
      throw error;
    }

    // 處理 403 權限不足錯誤
    // 後端可能回傳 { success: false, message: '<error_code>', interpolations: {...} }
    // 優先用 error code 查 t('errors.<code>')，找不到則 fallback 到通用訊息
    if (response.status === 403) {
      const body = await response
        .clone()
        .json()
        .catch(() => null);
      const code: string | undefined = body?.message;
      if (code) {
        const i18nKey = `errors.${code}`;
        const translated = i18n.t(i18nKey, body?.interpolations || {}) as string;
        if (translated && translated !== i18nKey) {
          message.error(translated);
        } else {
          message.error(i18n.t('common.error.permission_denied'));
        }
      } else {
        message.error(i18n.t('common.error.permission_denied'));
      }
      throw new Error('Forbidden');
    }

    // 422 (Unprocessable Content) 是驗證錯誤，返回 response 讓調用方處理
    if (!response.ok && response.status !== 422) {
      const body = await response
        .clone()
        .json()
        .catch(() => null);
      const error = new Error(
        response.statusText || `Request failed with status ${response.status}`
      ) as Error & {
        status?: number;
        errorCode?: string;
        interpolations?: Record<string, unknown>;
      };
      error.status = response.status;
      if (body?.message) {
        error.errorCode = body.message;
      }
      if (body?.interpolations) {
        error.interpolations = body.interpolations;
      }
      throw error;
    }

    return response;
  } catch (err) {
    console.error('Fetch error:', err);
    throw err;
  }
};

export default operatorApiFetch;
